<template>
  <div class="color-find-wrap">
    <div class="color-find-inner">
      <!-- 검색 영역 -->
      <div class="c-search" data-layer-hidden>
        <h2 class="sr-only">검색 영역</h2>
        <form class="search-form" @submit.prevent="handleSearch">
          <input
            v-model="searchKeyword"
            type="text"
            class="search-input"
            placeholder="색상명/색상코드 검색"
            @keydown.enter="handleSearch"
          />
          <button type="submit" class="search-submit">
            <span class="sr-only">검색</span>
          </button>
        </form>
      </div>

      <ul class="c-dot-list only-pc">
        <li>화면상의 색상과 실제 페인트의 색상간 차이가 있습니다.</li>
        <li>보다 정확한 색상은 실물 컬러북으로 확인하실 수 있습니다</li>
      </ul>

      <!-- 탭 메뉴 -->
      <div class="c-square-tabs col2">
        <ul id="tabs02">
          <li
            v-for="company in ['CGI', 'CVR', 'NR', 'TPG']"
            :key="company"
            class="tab-item"
          >
            <button
              type="button"
              class="tab-anchor"
              :class="{ 'is-active': selectedCompany === company }"
              @click="selectedCompany = company"
            >
              {{
                company === "CGI"
                  ? "노루컬러가이드"
                  : company === "CVR"
                    ? "Cover all"
                    : company === "NR"
                      ? "NR(Anycolor)"
                      : "PANTONE"
              }}
            </button>
          </li>
        </ul>
      </div>

      <!-- 색상 선택 영역 -->
      <div class="color-chip-wrap">
        <div class="color-solution">
          <div
            class="list_wrap"
            v-html="colorList"
            @click="handleColorChipClick"
          ></div>
        </div>
        <ul class="c-dot-list only-mo">
          <li>화면상의 색상과 실제 페인트의 색상간 차이가 있습니다.</li>
          <li>보다 정확한 색상은 실물 컬러북으로 확인하실 수 있습니다</li>
        </ul>

        <!-- 선택된 색상 표시 -->
        <div class="color-selected">
          <div class="color-selected-head">
            <h2 class="c-title-level5">선택한 색상</h2>
            <button type="button" class="btn-reset" @click="handleResetChart">
              초기화
            </button>
          </div>
          <div class="color-slot">
            <div class="c-color-item">
              <div class="color-palette">
                <div
                  class="color-value"
                  :style="{
                    background: selectedColor
                      ? selectedColor.background
                      : '#fff',
                  }"
                ></div>
              </div>
              <div class="color-info">
                <strong
                  class="color-name"
                  v-html="selectedColor ? selectedColor.name : ''"
                ></strong>
                <button
                  v-if="selectedColor"
                  class="btn-copy"
                  @click="handleCopyColorCode"
                >
                  복사
                </button>
              </div>
            </div>
          </div>
          <!-- D: mo -->
          <div v-if="selectedColor" class="btn-area ar only-mo">
            <button type="button" class="btn-copy" @click="handleCopyColorCode">
              <span>복사</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 최근 본 색상: PC -->
    <div class="c-recent-color">
      <div class="colorSelect">
        <div class="recent-color-inner">
          <h2 class="c-title-level5">최근 본 색상</h2>
          <div class="color-selected-list">
            <div
              v-for="(color, index) in recentColors"
              :key="color.id"
              class="c-color-item"
              data-color-viewed
            >
              <button
                type="button"
                class="color-palette"
                @click="handleColorCodeCopy(index)"
              >
                <div
                  class="color-value"
                  :style="{ backgroundColor: color.rgb }"
                ></div>
                <!-- D: over, click시 노출 -->
                <span class="text">{{
                  color.actionText === "" ? color.number : color.actionText
                }}</span>
              </button>
              <div class="color-info">
                <strong
                  class="color-name"
                  v-html="
                    formatColorName(color.company, color.number, color.name)
                  "
                />
              </div>
              <button
                type="button"
                class="btn-delete"
                @click.prevent="removeRecentColor(color.id)"
              >
                <i class="c-icon icon-medium-x size-small"></i>
                <span class="sr-only">컬러 삭제</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- // 최근 본 색상: PC -->
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useToast } from "@/components/paintpro/composables/useToast";
import * as cmUtil from "@/utils/cmUtil.js";
const { isMobile } = cmUtil.useDeviceType();

// 상태 관리를 위한 ref 선언
const selectedCompany = ref("CGI");
const searchKeyword = ref("");
const selectedColor = ref(null);
const colorList = ref([]);
const recentColors = ref([]);

// API 호출 함수
const fetchColorData = async (company, keyword = "") => {
  try {
    const response = await fetch(
      "https://www.noroopaint.com/kor/color/color_chip_ajax.asp",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `cmp=${company}&keyword=${keyword}`,
      }
    );
    const data = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, "text/html");
    const listWrapContent = doc.querySelector(".list_wrap").innerHTML;
    colorList.value = listWrapContent;
  } catch (error) {
    console.error("Error fetching color data:", error);
  }
};

// 색상 선택 처리
const handleColorSelect = (color) => {
  const { rgb, name, number, company } = color;

  selectedColor.value = {
    background: rgb,
    name: formatColorName(company, number, name),
    code: number,
    actionText: "",
    clickCount: 0,
  };

  // 최근 본 색상에 추가
  addToRecentColors(color);
};

const colorTost = (msg) => {
  const { showToast } = useToast();
  showToast(msg);
};

// ColorPicker.vue script 부분에 추가
const handleColorChipClick = (event) => {
  // 클릭된 요소가 li 태그인지 확인
  const clickedLi = event.target.closest("li");
  if (!clickedLi) return;

  // 컬러 정보 추출
  const colorData = {
    rgb: clickedLi.style.backgroundColor,
    name: clickedLi.dataset.name,
    number: clickedLi.dataset.number,
    company: clickedLi.dataset.company,
    actionText: "",
    clickCount: 0,
  };

  // 선택된 회사가 현재 탭과 다르면 탭 변경
  if (colorData.company !== selectedCompany.value) {
    selectedCompany.value = colorData.company;
  }

  // 색상 선택 처리
  handleColorSelect(colorData);

  // li 요소가 리스트 뷰포트 밖에 있는 경우 스크롤 조정
  const listWrap = document.querySelector(".list_wrap");
  const liRect = clickedLi.getBoundingClientRect();
  const wrapRect = listWrap.getBoundingClientRect();
  const allLi = listWrap.querySelectorAll("li");

  clickedLi.classList.add("is-active");
  allLi.forEach((li) => {
    if (li !== clickedLi) {
      li.classList.remove("is-active");
    }
  });

  if (liRect.top < wrapRect.top) {
    listWrap.scrollTop -= wrapRect.top - liRect.top + 10;
  } else if (liRect.bottom > wrapRect.bottom) {
    listWrap.scrollTop += liRect.bottom - wrapRect.bottom + 10;
  }
};

const removeRecentColor = (id) => {
  recentColors.value = recentColors.value.filter((color) => color.id !== id);
};

// 색상 이름 포맷팅
const formatColorName = (company, number, name) => {
  switch (company) {
    case "NR":
      return `NOROO <br/> ${number}`;
    case "CVR":
    case "CGI":
      return `NOROO <br/> ${number} <br/> ${name}`;
    case "TPG":
      return `PANTONE <br/> ${number} <br/> ${name}`;
    case "DLX":
      return `DULUX <br/> ${number} <br/> ${name}`;
    default:
      return `${number} ${name}`;
  }
};

// 최근 본 색상 관리
const addToRecentColors = (color) => {
  const newRecentColor = {
    ...color,
    id: Date.now(),
  };

  if (isMobile.value) {
    recentColors.value = [newRecentColor, ...recentColors.value.slice(0, 2)];
  } else {
    recentColors.value = [newRecentColor, ...recentColors.value.slice(0, 3)];
  }
};

// 색상코드 복사
const handleCopyColorCode = () => {
  if (!selectedColor.value?.code) {
    colorTost("컬러를 선택하세요");
    return;
  }

  navigator.clipboard
    .writeText(selectedColor.value.code)
    .then(() => colorTost("색상코드가 복사되었습니다."))
    .catch((err) => console.error("복사 실패:", err));
};

// 초기화
const handleResetChart = () => {
  searchKeyword.value = "";
  selectedColor.value = null;
  fetchColorData(selectedCompany.value);
};

// 검색 이벤트
const handleSearch = () => {
  fetchColorData(selectedCompany.value, searchKeyword.value);
};

// 최근 본 색상 코드 카피
const handleColorCodeCopy = (index) => {
  const item = recentColors.value[index];
  item.clickCount = (item.clickCount + 1) % 3;

  switch (item.clickCount) {
    case 1:
      item.actionText = "복사";
      break;

    case 2:
      item.actionText = "복사 완료";

      // 색상 코드 복사
      navigator.clipboard
        .writeText(item.number)
        .then(() => {})
        .catch((err) => {
          console.error("복사 실패:", err);
        });
      break;

    case 0:
      // 세 번째 클릭 (초기화)
      item.actionText = item.number;
      break;
  }
};

// 회사 변경 감시
watch(selectedCompany, (newCompany) => {
  searchKeyword.value = "";
  fetchColorData(newCompany);
});

// 컴포넌트 마운트 시 초기 데이터 로드
onMounted(() => {
  fetchColorData(selectedCompany.value);
});
</script>

<style lang="scss" scoped>
.list_wrap {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  position: relative;
  &:deep(.chip_list) {
    box-sizing: border-box;
    width: 60.5px;
    float: left;
    margin: 0 1px 0 0;
    ul {
      box-sizing: border-box;
      width: 60.5px;
    }

    li {
      border-radius: 2px;
      &.is-active {
        a {
          box-sizing: border-box;
          border: 2px solid #ffffff;
          &::after {
            border: 1px solid #fff;
            width: 57.7px;
            height: 57.7px;
          }
        }
      }
      a {
        display: block;
        box-sizing: border-box;
        width: 60.5px;
        height: 60.5px;
        margin: 0 0 1px 0;
        padding: 0;
        &:hover {
          box-sizing: border-box;
          border: 2px solid #ffffff;
          &::after {
            border: 1px solid #fff;
            width: 57.7px;
            height: 57.7px;
          }
        }
      }
    }
  }
}
</style>
